import React, { useCallback, useMemo } from 'react';

import ChatIcon from '@atlaskit/icon/core/ai-chat';
import EditIcon from '@atlaskit/icon/core/edit';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { useRovoNavigation } from '@atlassian/conversation-assistant/controllers/rovo-navigation';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useIntl } from 'react-intl';

import { type CommonSideNavItemProps } from '@townsquare/griffin-navigation/types';
import { useRouterActions } from '@townsquare/router/primitives/actions';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { generatePath } from '@townsquare/router/primitives/path';
import { useRouter } from '@townsquare/router/useRouter';
import { chatRoute, createChatRouteQuery } from '@townsquare/routes/chat';
import { useAssistanceServiceParams } from '@townsquare/rovo/hooks';
import { appendSearchStringToPath } from '@townsquare/search-params';
import { fg } from '@townsquare/stat-sig/gate';

export const RovoChatNavItem = (props: CommonSideNavItemProps): React.JSX.Element => {
	const { onClick, item } = props;
	const intl = useIntl();
	const { pushTo } = useRouterActions();
	const [routerStore] = useRouter();
	const { activeMenu, setActiveMenu } = useUIStore();
	const publish = usePublish('ai-mate');
	const routeDependencies = useRouteDependencies();

	const assistanceServiceParams = useAssistanceServiceParams({
		experienceId: 'ai-mate',
		touchpointSource: 'townsquare-sidenav',
	});
	const { currentConversation, handleNewChatClick } = useRovoNavigation({
		assistanceServiceParams,
		isTasksEnabled: fg('rovo_chat_enable_tasks'),
	});

	const isOnChatRoute = !!matchRoute([chatRoute], routerStore.location.pathname, undefined);
	const { rovoChatConversationId, rovoChatPathway, rovoChatSpaceId } = routerStore.query;
	const hasSelectedChildRoute = Boolean(
		rovoChatConversationId ||
		rovoChatSpaceId ||
		rovoChatPathway === 'all-conversations' ||
		rovoChatPathway === 'all-spaces' ||
		rovoChatPathway === 'recommended-spaces' ||
		rovoChatPathway === 'library',
	);
	const isSelected =
		isOnChatRoute &&
		activeMenu === 'none' &&
		!hasSelectedChildRoute &&
		(currentConversation === undefined || currentConversation.isEmpty);

	const chatQuery = useMemo(() => createChatRouteQuery(routerStore.query), [routerStore.query]);
	// eslint-disable-next-line no-restricted-syntax
	const chatSearchParams = new URLSearchParams(chatQuery);
	const chatHref = appendSearchStringToPath(
		generatePath(chatRoute, routeDependencies),
		chatSearchParams.toString(),
	);

	const onChatClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			setActiveMenu('none');
			pushTo(chatRoute, { query: chatQuery });
			publish({
				type: 'space-deselected',
				source: 'rovo-sidebar-navigation',
				openChat: false,
			});
			handleNewChatClick(true);
			onClick(item, e);
		},
		[publish, pushTo, handleNewChatClick, item, onClick, chatQuery, setActiveMenu],
	);

	return (
		<LinkMenuItem
			href={chatHref}
			elemBefore={
				fg('townsquare_teameu_rovo_nav_changes') ? (
					<EditIcon label="" color="currentColor" />
				) : (
					<ChatIcon label="" color="currentColor" />
				)
			}
			onClick={onChatClick}
			isSelected={isSelected}
			testId="side-nav-chat"
		>
			{fg('townsquare_teameu_rovo_nav_changes')
				? intl.formatMessage({
						id: 'townsquare.home.use-rovo-side-nav-items.new-chat',
						description: 'Side nav button to go to new chat page',
						defaultMessage: 'New chat',
					})
				: intl.formatMessage({
						id: 'townsquare.atlassian-home.side-nav.chat',
						description: 'Side nav button to open chat page',
						defaultMessage: 'Chat',
					})}
		</LinkMenuItem>
	);
};
